import { acceptedForms } from "./answer.js";
import { norm } from "./text.js";

/**
 * Adding words to a list that already has some -- a second scan, pasting
 * with «Hinzufügen» -- must not double a pair (issue #83). A pair is the same
 * when both sides match after `norm`, as in the worker's term key, and on top
 * of that the order and repetition of alternatives do not matter: «cher |
 * chère» and «chère | cher» are one pair here, two terms in the worker. So
 * this rule is a little stricter than the worker's, never looser: whatever the
 * worker would fold on save, it folds too. A different translation is a
 * different pair: «kosten = coûter» and «kosten = goûter» both stay.
 */
export function pairKey(a: string, b: string): string {
  const forms = [...new Set(acceptedForms(b).map(norm))].sort();
  return `${norm(a)}\u0000${forms.join("|")}`;
}

export interface MergeResult<T> {
  /** The incoming pairs that are new, in their original order. */
  added: T[];
  /** How many incoming pairs were already there, or twice in the batch. */
  skipped: number;
}

export function mergePairs<T extends { a: string; b: string }>(
  existing: ReadonlyArray<{ a: string; b: string }>,
  incoming: readonly T[],
): MergeResult<T> {
  const seen = new Set(existing.filter(isComplete).map((pair) => pairKey(pair.a, pair.b)));
  const added: T[] = [];
  for (const pair of incoming) {
    // A scan row with an unreadable side is a word still to be filled in, not
    // a repeat of another unreadable one.
    if (!isComplete(pair)) {
      added.push(pair);
      continue;
    }
    const key = pairKey(pair.a, pair.b);
    if (seen.has(key)) continue;
    seen.add(key);
    added.push(pair);
  }
  return { added, skipped: incoming.length - added.length };
}

/**
 * For words typed by hand: which rows repeat a pair from further up. Nothing
 * is removed -- the child decides -- the row only gets a hint. Rows with an
 * empty side are never duplicates; they are still being typed.
 */
export function repeatedRows(rows: ReadonlyArray<{ a: string; b: string }>): Set<number> {
  const seen = new Set<string>();
  const repeated = new Set<number>();
  rows.forEach((row, index) => {
    if (!isComplete(row)) return;
    const key = pairKey(row.a, row.b);
    if (seen.has(key)) repeated.add(index);
    else seen.add(key);
  });
  return repeated;
}

function isComplete(pair: { a: string; b: string }): boolean {
  return norm(pair.a) !== "" && acceptedForms(pair.b).length > 0;
}
